import { Component } from '@theme/component';

/**
 * @typedef {Object} AxFaqRefs
 * @property {HTMLButtonElement[]} [tags] - Pill buttons (tablist).
 * @property {HTMLElement[]} [panels] - Accordion panels, one per category.
 */

/**
 * @extends {Component<AxFaqRefs>}
 */
class AxFaqComponent extends Component {
  #activeIndex = 0;

  connectedCallback() {
    super.connectedCallback();
  }

  /**
   * @param {string} indexStr - Index passed via `on:click="/handleTagClick/{index}"`.
   */
  handleTagClick(indexStr) {
    const index = parseInt(indexStr, 10);
    if (Number.isNaN(index)) return;
    this.#selectTab(index);
  }

  /**
   * @param {KeyboardEvent} event
   */
  handleTagKeydown(event) {
    const tags = this.refs.tags;
    if (!tags?.length) return;

    let nextIndex = this.#activeIndex;
    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = (this.#activeIndex - 1 + tags.length) % tags.length;
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = (this.#activeIndex + 1) % tags.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = tags.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#selectTab(nextIndex);
    tags[nextIndex]?.focus();
  }

  /**
   * @param {number} index
   */
  #selectTab(index) {
    const { tags, panels } = this.refs;
    if (!tags?.length || !panels?.length) return;
    if (index === this.#activeIndex) return;

    for (const [i, tag] of tags.entries()) {
      const isActive = i === index;
      tag.classList.toggle('is-active', isActive);
      tag.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tag.setAttribute('tabindex', isActive ? '0' : '-1');
    }

    for (const [i, panel] of panels.entries()) {
      panel.hidden = i !== index;
    }

    this.#resetFirstOpen(panels[index]);
    this.#activeIndex = index;
  }

  /**
   * Au switch de catégorie : ouvre la 1ère question si `data-open-first` est à 'true',
   * sinon referme tout.
   *
   * @param {HTMLElement | undefined} panel
   */
  #resetFirstOpen(panel) {
    if (!panel) return;
    const openFirst = this.dataset.openFirst === 'true';
    const items = panel.querySelectorAll('.ax-faq__item');
    for (const [i, item] of items.entries()) {
      if (item instanceof HTMLDetailsElement) {
        item.open = openFirst && i === 0;
      }
    }
  }
}

if (!customElements.get('ax-faq')) {
  customElements.define('ax-faq', AxFaqComponent);
}
